/**
 * Medical Dictionary Manager Component
 * Manage dictionary entries for text normalization in detection pipeline
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Alert,
  Tooltip,
  Stack,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudDownload as CsvExportIcon,
  CloudUpload as CsvImportIcon,
} from '@mui/icons-material';
import dictionaryApi, {
  DictionaryEntry,
  CreateDictionaryEntry,
} from '../../services/dictionaryApi';

const ENTITY_TYPES = [
  { value: 'anatomy', label: 'Anatomie', color: '#2196f3' },
  { value: 'modality', label: 'Modalitate', color: '#4caf50' },
  { value: 'contrast', label: 'Contrast', color: '#ff9800' },
  { value: 'technique', label: 'Tehnică', color: '#9c27b0' },
];

export const DictionaryManager: React.FC = () => {
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showMessage = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // State
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedEntry, setSelectedEntry] = useState<DictionaryEntry | null>(null);
  
  // CSV Import state
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace' | 'update'>('skip');
  const [importing, setImporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateDictionaryEntry>({
    term_ro: '',
    term_canonical: '',
    entity_type: 'anatomy',
    source: 'manual',
  });

  // Load entries
  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await dictionaryApi.listDictionaryEntries({
        entity_type: entityTypeFilter || undefined,
        search: searchFilter || undefined,
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      });
      
      setEntries(response.items);
      setTotal(response.total);
    } catch (error: any) {
      showMessage(error.message || 'Eroare la încărcarea dicționarului', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, [page, rowsPerPage, entityTypeFilter, searchFilter]);

  // Handlers
  const handleCreate = () => {
    setDialogMode('create');
    setFormData({
      term_ro: '',
      term_canonical: '',
      entity_type: 'anatomy',
      source: 'manual',
    });
    setOpenDialog(true);
  };

  const handleEdit = (entry: DictionaryEntry) => {
    setDialogMode('edit');
    setSelectedEntry(entry);
    setFormData({
      term_ro: entry.term_ro,
      term_canonical: entry.term_canonical,
      entity_type: entry.entity_type,
      source: entry.source,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (entry: DictionaryEntry) => {
    if (!window.confirm(`Ștergi "${entry.term_ro}" → "${entry.term_canonical}"?`)) {
      return;
    }

    try {
      await dictionaryApi.deleteDictionaryEntry(entry.id, 'Deleted via UI');
      showMessage('Intrare ștearsă cu succes', 'success');
      loadEntries();
    } catch (error: any) {
      showMessage(error.message || 'Eroare la ștergerea intrării', 'error');
    }
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await dictionaryApi.createDictionaryEntry(formData);
        showMessage('Intrare creată cu succes', 'success');
      } else if (selectedEntry) {
        await dictionaryApi.updateDictionaryEntry(selectedEntry.id, {
          term_canonical: formData.term_canonical,
        });
        showMessage('Intrare actualizată cu succes', 'success');
      }
      
      setOpenDialog(false);
      loadEntries();
    } catch (error: any) {
      showMessage(error.message || 'Operațiune eșuată', 'error');
    }
  };

  const handleExportCsv = async () => {
    try {
      const blob = await dictionaryApi.exportDictionaryCsv(entityTypeFilter || undefined);
      
      // Download CSV
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dictionary-${entityTypeFilter || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      showMessage('CSV exportat cu succes - deschide în Excel pentru editare', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Export CSV eșuat', 'error');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setImporting(true);

    try {
      const preview = await dictionaryApi.previewCsvImport(file);
      setCsvPreview(preview);
      setOpenImportDialog(true);
    } catch (error: any) {
      showMessage(error.message || 'Eroare la previzualizarea CSV', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImportCsv = async () => {
    if (!csvFile) return;

    setImporting(true);
    try {
      const result = await dictionaryApi.importDictionaryCsv(csvFile, importMode);
      
      showMessage(
        `Import: ${result.created} create, ${result.updated} actualizate, ${result.skipped} omise`,
        'success'
      );
      
      setOpenImportDialog(false);
      setCsvFile(null);
      setCsvPreview(null);
      loadEntries();
    } catch (error: any) {
      showMessage(error.message || 'Import eșuat', 'error');
    } finally {
      setImporting(false);
    }
  };

  const getEntityTypeColor = (type: string) => {
    return ENTITY_TYPES.find(t => t.value === type)?.color || '#666';
  };

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  };
  const tableSx = {
    '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
    '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
    '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
  };

  return (
    <Paper sx={{ p: 1.5 }}>
      {/* Header - Compact */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
          Dicționar Medical ({total} termeni)
        </Typography>
        
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Exportă în CSV (Excel)">
            <IconButton onClick={handleExportCsv} size="small" color="primary" sx={{ p: 0.5 }}>
              <CsvExportIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Importă din CSV">
            <IconButton component="label" size="small" color="primary" sx={{ p: 0.5 }}>
              <CsvImportIcon sx={{ fontSize: 18 }} />
              <input type="file" accept=".csv" hidden onChange={handleFileSelect} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reîncarcă">
            <IconButton onClick={loadEntries} size="small" sx={{ p: 0.5 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
            Adaugă Termen
          </Button>
        </Stack>
      </Box>

      {/* Filters - Compact inline */}
      <Box display="flex" gap={1} mb={1.5} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140, ...inputSx }}>
          <InputLabel>Tip Entitate</InputLabel>
          <Select value={entityTypeFilter} label="Tip Entitate" onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0); }}>
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate Tipurile</MenuItem>
            {ENTITY_TYPES.map((type) => (<MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem' }}>{type.label}</MenuItem>))}
          </Select>
        </FormControl>
        <TextField size="small" label="Căutare (RO sau EN)" value={searchFilter} onChange={(e) => { setSearchFilter(e.target.value); setPage(0); }} sx={{ flex: 1, ...inputSx }} />
      </Box>

      {/* Info Alert - Compact */}
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem', bgcolor: 'info.dark', color: 'info.contrastText', '& .MuiAlert-icon': { color: 'info.contrastText' } }}>
        Intrările din dicționar sunt folosite pentru normalizarea textului în pipeline-ul de detecție. Modificările sunt cache-uite (1h TTL) și afectează toate detecțiile noi.
      </Alert>

      {/* Table - Compact */}
      <TableContainer sx={tableSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
              <TableCell>Tip</TableCell>
              <TableCell>Termen (RO)</TableCell>
              <TableCell>Canonic (EN)</TableCell>
              <TableCell>Sursă</TableCell>
              <TableCell>Adăugat</TableCell>
              <TableCell align="right">Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ fontSize: '0.78rem' }}>Se încarcă...</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ fontSize: '0.78rem' }}>Nu s-au găsit intrări</TableCell></TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell><Chip label={entry.entity_type} size="small" sx={{ bgcolor: getEntityTypeColor(entry.entity_type), color: 'white', height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell><strong>{entry.term_ro}</strong></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{entry.term_canonical}</TableCell>
                  <TableCell><Chip label={entry.source} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{new Date(entry.added_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(entry)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(entry)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination - Compact */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }, '& .MuiTablePagination-select': { fontSize: '0.75rem' } }}
      />

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Adaugă Intrare în Dicționar' : 'Editează Intrare în Dicționar'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth>
              <InputLabel>Tip Entitate</InputLabel>
              <Select
                value={formData.entity_type}
                label="Tip Entitate"
                onChange={(e) => setFormData({ ...formData, entity_type: e.target.value as any })}
                disabled={dialogMode === 'edit'}
              >
                {ENTITY_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Termen (Română)"
              value={formData.term_ro}
              onChange={(e) => setFormData({ ...formData, term_ro: e.target.value })}
              placeholder="craniu, sold, nativ"
              disabled={dialogMode === 'edit'}
              fullWidth
            />

            <TextField
              label="Cod Canonic (Engleză)"
              value={formData.term_canonical}
              onChange={(e) => setFormData({ ...formData, term_canonical: e.target.value.toUpperCase() })}
              placeholder="BRAIN, HIP, WITHOUT"
              fullWidth
            />

            <Alert severity="info" sx={{ '& .MuiAlert-message': { color: '#fff' } }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>
                <strong>Sinonime:</strong> Mai mulți termeni RO pot fi mapați la același cod canonic (ex: "craniu" și "encefal" ambii → "BRAIN")
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Anulează</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.term_ro || !formData.term_canonical}
          >
            {dialogMode === 'create' ? 'Creează' : 'Actualizează'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Preview Dialog */}
      <Dialog 
        open={openImportDialog} 
        onClose={() => !importing && setOpenImportDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle>
          Previzualizare Import CSV
          {csvFile && ` - ${csvFile.name}`}
        </DialogTitle>
        <DialogContent>
          {csvPreview && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              {/* Summary */}
              <Alert severity={csvPreview.is_valid ? 'success' : 'error'}>
                <Typography variant="body2" fontWeight="bold">
                  {csvPreview.valid_entries} intrări valide, {csvPreview.error_count} erori, {csvPreview.warning_count} avertizări
                </Typography>
              </Alert>

              {/* Errors */}
              {csvPreview.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Erori:
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflow: 'auto', bgcolor: '#fff3f3', p: 1, borderRadius: 1 }}>
                    {csvPreview.errors.map((error: string, idx: number) => (
                      <Typography key={idx} variant="caption" display="block" color="error">
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Warnings */}
              {csvPreview.warnings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="warning.main" gutterBottom>
                    Avertizări:
                  </Typography>
                  <Box sx={{ maxHeight: 100, overflow: 'auto', bgcolor: '#fff8e1', p: 1, borderRadius: 1 }}>
                    {csvPreview.warnings.map((warning: string, idx: number) => (
                      <Typography key={idx} variant="caption" display="block" color="warning.dark">
                        • {warning}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Import Mode */}
              <FormControl fullWidth>
                <InputLabel>Mod Import</InputLabel>
                <Select
                  value={importMode}
                  label="Mod Import"
                  onChange={(e) => setImportMode(e.target.value as any)}
                >
                  <MenuItem value="skip">Omite existente (cel mai sigur)</MenuItem>
                  <MenuItem value="update">Actualizează existente (doar canonic)</MenuItem>
                  <MenuItem value="replace">Înlocuiește existente (șterge + recreează)</MenuItem>
                </Select>
              </FormControl>

              {/* Preview entries */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Previzualizare (primele {Math.min(csvPreview.entries.length, 100)} intrări):
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rând</TableCell>
                        <TableCell>Tip</TableCell>
                        <TableCell>Termen (RO)</TableCell>
                        <TableCell>Canonic (EN)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvPreview.entries.slice(0, 10).map((entry: any) => (
                        <TableRow key={entry.row_number}>
                          <TableCell>{entry.row_number}</TableCell>
                          <TableCell>
                            <Chip label={entry.entity_type} size="small" />
                          </TableCell>
                          <TableCell>{entry.term_ro}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {entry.term_canonical}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportDialog(false)} disabled={importing}>
            Anulează
          </Button>
          <Button
            onClick={handleImportCsv}
            variant="contained"
            disabled={importing || !csvPreview?.is_valid}
            startIcon={importing ? <CircularProgress size={16} /> : <CsvImportIcon />}
          >
            {importing ? 'Se importă...' : 'Importă'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

